import { useDebounceFn } from '@vueuse/core'

import type { WritableComputedRef } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'

import { useProfileStore } from '@entities/viewer'

import { useApiTokensStore } from './store'
import { useApiTokensApi } from '../../api/api-tokens/list.hook'

const SEARCH_DEBOUNCE_MS = 400

interface FiltersApiTokensReturn {
  refUserId: WritableComputedRef<number | undefined>
  refSearch: WritableComputedRef<string>
  userOptions: ComputedRef<AppSelectOption<number>[]>
  canFilterByUser: ComputedRef<boolean>
  isLoading: ComputedRef<boolean>
  openCreateModal: () => void
}

export const useFiltersApiTokens = (): FiltersApiTokensReturn => {
  const router = useRouter()
  const route = useRoute()
  const store = useApiTokensStore()
  const profileStore = useProfileStore()

  const syncQueryAndReload = async (): Promise<void> => {
    await router.push({
      query: {
        ...route.query,
        user_id: store.getFilterUserId ? String(store.getFilterUserId) : undefined,
        search: store.getFilterSearch || undefined,
        page: undefined,
      },
    })
    await useApiTokensApi()
  }

  const refUserId = computed<number | undefined>({
    get: () => store.getFilterUserId ?? undefined,
    set: (value) => {
      store.setFilterUserId(value ?? null)
      void syncQueryAndReload()
    },
  })

  const applySearch = useDebounceFn(() => syncQueryAndReload(), SEARCH_DEBOUNCE_MS)

  const refSearch = computed<string>({
    get: () => store.getFilterSearch ?? '',
    set: (value) => {
      store.setFilterSearch(value.trim() || null)
      void applySearch()
    },
  })

  const userOptions = computed<AppSelectOption<number>[]>(() => store.getUserOptions)
  const canFilterByUser = computed<boolean>(() =>
    profileStore.privileges?.includes('manage-all-api-tokens') ?? false,
  )
  const isLoading = computed<boolean>(() => store.isLoading)

  const openCreateModal = (): void => {
    store.setModalCreate(true)
  }

  return {
    refUserId,
    refSearch,
    userOptions,
    canFilterByUser,
    isLoading,
    openCreateModal,
  }
}
